import { useEffect, useState } from 'react';

import { Button } from 'components/Button';
import { Icon } from 'components/Icon';
import { Modal } from 'components/Modal';
import { commonStrings, lineStrings } from 'constants/strings';
import { ELineChannelStatus } from 'types';

type BulkStatusModalProps = {
  open: boolean;
  selectedCount: number;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (status: ELineChannelStatus) => void;
};

const STATUS_OPTIONS: {
  value: ELineChannelStatus;
  labelKey: keyof typeof lineStrings.edit.statusOptions;
  descKey: keyof typeof lineStrings.management.bulk.statusModal.options;
}[] = [
  { value: ELineChannelStatus.ACTIVE, labelKey: 'active', descKey: 'active' },
  { value: ELineChannelStatus.PAUSED, labelKey: 'paused', descKey: 'paused' },
  { value: ELineChannelStatus.FULL, labelKey: 'full', descKey: 'full' },
];

export function BulkStatusModal({
  open,
  selectedCount,
  isSubmitting,
  onClose,
  onSubmit,
}: BulkStatusModalProps) {
  const strings = lineStrings.management.bulk.statusModal;
  const statusOptions = lineStrings.edit.statusOptions;
  const [status, setStatus] = useState<ELineChannelStatus>(
    ELineChannelStatus.ACTIVE,
  );

  useEffect(() => {
    if (open) {
      setStatus(ELineChannelStatus.ACTIVE);
    }
  }, [open]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className="grid gap-5">
        <div className="grid gap-1.5">
          <h2 className="text-lg font-semibold text-ink">{strings.title}</h2>
          <p className="text-sm text-muted">
            {strings.description.replace('{{count}}', String(selectedCount))}
          </p>
        </div>
        <fieldset className="grid gap-2.5" disabled={isSubmitting}>
          <legend className="sr-only">{strings.title}</legend>
          {STATUS_OPTIONS.map((option) => {
            const selected = status === option.value;
            return (
              <button
                type="button"
                key={option.value}
                onClick={() => setStatus(option.value)}
                aria-pressed={selected}
                className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition disabled:cursor-not-allowed disabled:opacity-60 ${
                  selected
                    ? 'border-accent bg-accent-soft ring-1 ring-accent/40'
                    : 'border-border/80 bg-white hover:border-accent/50'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                    selected
                      ? 'border-accent bg-accent text-white'
                      : 'border-border'
                  }`}
                >
                  {selected && <Icon icon="check" width={12} height={12} />}
                </span>
                <span className="grid gap-0.5">
                  <span className="text-sm font-semibold text-ink">
                    {statusOptions[option.labelKey]}
                  </span>
                  <span className="text-xs text-muted">
                    {strings.options[option.descKey]}
                  </span>
                </span>
              </button>
            );
          })}
        </fieldset>
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={isSubmitting}
            onClick={handleClose}
          >
            {commonStrings.cancel}
          </Button>
          <Button
            type="button"
            className="flex-1"
            loading={isSubmitting}
            disabled={isSubmitting}
            onClick={() => onSubmit(status)}
          >
            {strings.submit}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
